/**
 * Comprehensive Tour Configurations for all HRIS Modules
 * Each module has detailed step-by-step walkthrough with helpful tips
 */

import type { TourStep } from '@/composables/useOnboarding'

// ============================================
// DASHBOARD TOUR - Welcome & Overview
// ============================================
export const dashboardTour: TourStep[] = [
  {
    popover: {
      title: '👋 Welcome to HRIS!',
      description:
        'This guided tour will help you navigate the system. You can restart this tour anytime from the help button in the bottom right corner.',
      side: 'over',
      align: 'center',
    },
  },
  {
    element: '.dashboard-stats, .grid',
    popover: {
      title: '📊 Dashboard Overview',
      description:
        'Your dashboard shows key metrics at a glance: total employees, attendance today, pending approvals, and more.',
      side: 'bottom',
    },
  },
  {
    element: '.va-sidebar, [class*="sidebar"]',
    popover: {
      title: '📋 Navigation Menu',
      description: 'Use the sidebar to navigate between modules: Employees, Attendance, Leave, Payroll, and more.',
      side: 'right',
    },
  },
  {
    element: '.va-navbar, [class*="navbar"], header',
    popover: {
      title: '🔔 Top Navigation',
      description: 'Access notifications, switch organizational units, and manage your profile from here.',
      side: 'bottom',
    },
  },
]

// ============================================
// EMPLOYEES TOUR - Employee Management
// ============================================
export const employeesTour: TourStep[] = [
  {
    element: '.page-title, .page-header, h1',
    popover: {
      title: '👥 Employee Management',
      description:
        'This module lets you manage all employee records - view, create, edit, and organize your workforce.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="search-input"]',
    popover: {
      title: '🔍 Search Employees',
      description:
        'Type to search by name, employee ID, email, or department. Press Enter or click Search to filter results.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="create-employee-btn"]',
    popover: {
      title: '➕ Add New Employee',
      description:
        "Click here to add a new employee. You'll be guided through entering personal info, employment details, and more.",
      side: 'left',
    },
  },
  {
    element: '.ag-root-wrapper',
    popover: {
      title: '📋 Employee List',
      description:
        'View all employees in this table. Click column headers to sort, use filters to narrow results, and double-click a row to view the full profile.',
      side: 'top',
    },
  },
]

// ============================================
// ATTENDANCE TOUR - Time Tracking
// ============================================
export const attendanceTour: TourStep[] = [
  {
    element: '.page-header',
    popover: {
      title: '⏰ Attendance & DTR',
      description: 'Track employee attendance, view daily time records, and manage clock in/out operations.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="clock-btn"]',
    popover: {
      title: '👆 Clock In/Out',
      description:
        'Click here to record your time in or time out. A modal will show the current time and your last recorded action.',
      side: 'left',
    },
  },
  {
    element: '.attendance-summary',
    popover: {
      title: "📈 Today's Summary",
      description: 'Quick overview showing: Total employees, Present, Absent, Late, and On Leave counts for today.',
      side: 'bottom',
    },
  },
  {
    element: '.va-tabs',
    popover: {
      title: '📑 View Options',
      description:
        'Switch between "Today\'s Attendance" for real-time status, and "Daily Time Record" for historical data.',
      side: 'bottom',
    },
  },
]

// ============================================
// PAYROLL TOUR - Salary Processing
// ============================================
export const payrollTour: TourStep[] = [
  {
    element: '.page-header',
    popover: {
      title: '💰 Payroll Management',
      description: 'Process employee salaries, manage payroll periods, calculate deductions, and generate payslips.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="create-period-btn"]',
    popover: {
      title: '📆 Create Payroll Period',
      description:
        'Start by creating a payroll period. Choose the month, period type (1st half, 2nd half, or monthly), and pay date.',
      side: 'left',
    },
  },
  {
    element: '.payroll-summary',
    popover: {
      title: '📊 Payroll Summary',
      description: 'View year-to-date totals: Gross Pay, Net Pay, total employees processed, and pending periods.',
      side: 'bottom',
    },
  },
  {
    element: '.va-tabs',
    popover: {
      title: '📑 Payroll Tabs',
      description: 'Switch between Payroll Periods (manage periods) and Payroll Records (view employee payslips).',
      side: 'bottom',
    },
  },
]

// ============================================
// LEAVE TOUR - Leave Management
// ============================================
export const leaveTour: TourStep[] = [
  {
    element: '.page-header',
    popover: {
      title: '🏖️ Leave Management',
      description: 'Request leaves, check your balances, and manage leave approvals for your team.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="apply-leave-btn"]',
    popover: {
      title: '✋ Request Leave',
      description: 'Click here to submit a new leave request. Select leave type, dates, and provide a reason.',
      side: 'left',
    },
  },
  {
    element: '.leave-balance',
    popover: {
      title: '📊 Your Leave Balances',
      description:
        'View your available leave credits for each type: Vacation, Sick, Emergency, etc. Shows used vs remaining days.',
      side: 'bottom',
    },
  },
  {
    element: '.va-tabs',
    popover: {
      title: '📑 Leave Tabs',
      description:
        'Navigate between: My Requests, All Requests (admin), Pending Approvals, Leave Types config, and Employee Balances.',
      side: 'bottom',
    },
  },
]

// ============================================
// OVERTIME TOUR - OT Management
// ============================================
export const overtimeTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '⏱️ Overtime Management',
      description: 'File overtime requests, track OT hours, and manage approvals for compensation.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="request-ot-btn"], button:has-text("Request")',
    popover: {
      title: '📝 File OT Request',
      description:
        'Submit a new overtime request. Specify date, hours, rate type (regular, night diff, holiday), and reason.',
      side: 'left',
    },
  },
  {
    element: '.ot-summary, .grid:first-of-type',
    popover: {
      title: '📊 OT Summary',
      description: 'View your total OT hours this month, pending requests, and approved OT ready for payroll.',
      side: 'bottom',
    },
  },
  {
    element: '.va-data-table, table',
    popover: {
      title: '📋 OT Requests',
      description:
        'Track all your overtime requests. See status: Pending approval, Approved, or Rejected with remarks.',
      side: 'top',
    },
  },
  {
    element: '[class*="compensation"]',
    popover: {
      title: '💵 Compensation Type',
      description: 'OT can be compensated as: Paid (added to payroll) or Offset (converted to time-off credits).',
      side: 'left',
    },
  },
]

// ============================================
// SHIFTS TOUR - Shift Configuration
// ============================================
export const shiftsTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '🕐 Shift Management',
      description: 'Define work shifts with specific time in/out schedules, break times, and grace periods.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="create-shift-btn"], button:has-text("Add"), button:has-text("Create")',
    popover: {
      title: '➕ Create Shift',
      description:
        'Define a new shift: Name (e.g., "Day Shift"), Time In (8:00 AM), Time Out (5:00 PM), break duration, and grace period.',
      side: 'left',
    },
  },
  {
    element: '.shifts-table, .va-data-table, table',
    popover: {
      title: '📋 Shift List',
      description:
        "View all configured shifts. Each shift shows time schedule, total hours, and whether it's the default.",
      side: 'top',
    },
  },
  {
    element: 'button[title="Edit"]',
    popover: {
      title: '✏️ Edit Shift',
      description:
        'Modify shift timings, grace periods, or break durations. Changes apply to future attendance calculations.',
      side: 'left',
    },
  },
]

// ============================================
// WORK SCHEDULES TOUR - Schedule Assignments
// ============================================
export const workSchedulesTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '📅 Work Schedules',
      description: 'Create and assign weekly work schedules defining which days employees work and their rest days.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="create-schedule-btn"], button:has-text("Add"), button:has-text("Create")',
    popover: {
      title: '➕ Create Schedule',
      description:
        'Define a work schedule pattern: Regular (Mon-Fri), Shifting, or Custom. Set work days and rest days.',
      side: 'left',
    },
  },
  {
    element: '.va-data-table, table',
    popover: {
      title: '📋 Schedule List',
      description: 'View all work schedules. Each shows the weekly pattern (e.g., Mon-Fri work, Sat-Sun off).',
      side: 'top',
    },
  },
  {
    element: 'button[title="Assign"]',
    popover: {
      title: '👥 Assign to Employees',
      description:
        'Assign a schedule to employees or departments. This determines their expected work days and rest days.',
      side: 'left',
    },
  },
]

// ============================================
// ORG CHART TOUR - Organization Structure
// ============================================
export const orgChartTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '🏢 Organization Chart',
      description: "Visualize your company's hierarchy and reporting structure in an interactive tree view.",
      side: 'bottom',
    },
  },
  {
    element: '.org-chart-container, [class*="orgchart"], svg',
    popover: {
      title: '🌳 Interactive Chart',
      description: 'Click on nodes to expand/collapse branches. Drag to pan around. Scroll or pinch to zoom in/out.',
      side: 'top',
    },
  },
  {
    element: 'button:has-text("Zoom"), [class*="zoom"]',
    popover: {
      title: '🔍 Zoom Controls',
      description: "Use zoom controls to get a bird's eye view or focus on specific branches of the organization.",
      side: 'left',
    },
  },
  {
    element: '[class*="node"], .org-node',
    popover: {
      title: '👤 Employee Nodes',
      description:
        'Each node shows: Employee name, position, and department. Click for quick actions like view profile.',
      side: 'right',
    },
  },
]

// ============================================
// REPORTS TOUR - Report Generation
// ============================================
export const reportsTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '📈 Reports Center',
      description: 'Generate, preview, and export various HR reports for analysis and compliance.',
      side: 'bottom',
    },
  },
  {
    element: '.report-categories, .va-card:first-of-type',
    popover: {
      title: '📊 Report Categories',
      description:
        'Choose from categories: Attendance Reports, Payroll Reports, Leave Reports, Employee Reports, and more.',
      side: 'right',
    },
  },
  {
    element: '[class*="filter"], .va-select',
    popover: {
      title: '🔧 Report Filters',
      description: 'Customize reports with filters: Date range, Department, Employee status, and other parameters.',
      side: 'bottom',
    },
  },
  {
    element: 'button:has-text("Generate"), button:has-text("Export")',
    popover: {
      title: '📥 Generate & Export',
      description: 'Generate report previews, then export to PDF or Excel for distribution or further analysis.',
      side: 'left',
    },
  },
]

// ============================================
// SETTINGS TOUR - System Configuration
// ============================================
export const settingsTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '⚙️ System Settings',
      description: 'Configure system-wide settings, preferences, and administrative options.',
      side: 'bottom',
    },
  },
  {
    element: '.settings-nav, .va-tabs',
    popover: {
      title: '📑 Settings Categories',
      description: 'Navigate between: General Settings, Company Profile, Email Config, Integrations, and more.',
      side: 'right',
    },
  },
  {
    element: '[class*="company"], [class*="profile"]',
    popover: {
      title: '🏢 Company Profile',
      description: 'Update company information: Name, address, logo, tax IDs, and other organizational details.',
      side: 'bottom',
    },
  },
  {
    element: 'button:has-text("Save")',
    popover: {
      title: '💾 Save Changes',
      description:
        "Don't forget to save after making changes. Some settings may require a page refresh to take effect.",
      side: 'left',
    },
  },
]

// ============================================
// USERS TOUR - User Account Management
// ============================================
export const usersTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '👤 User Management',
      description: 'Manage system users who can log in to HRIS. Users are linked to employees for self-service access.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="create-user-btn"], button:has-text("Add"), button:has-text("Create")',
    popover: {
      title: '➕ Create User',
      description: 'Add a new system user. Provide username, email, password, and link to an employee record.',
      side: 'left',
    },
  },
  {
    element: '.va-data-table, table',
    popover: {
      title: '📋 User List',
      description: 'View all system users with their roles, linked employee, and last login information.',
      side: 'top',
    },
  },
  {
    element: '[class*="role"], .va-select',
    popover: {
      title: '🔐 User Roles',
      description: 'Assign roles to control what each user can access: Admin, HR Manager, Employee, etc.',
      side: 'left',
    },
  },
  {
    element: 'button[title="Reset Password"]',
    popover: {
      title: '🔑 Password Reset',
      description: "Reset a user's password if they forget it. They'll receive a temporary password via email.",
      side: 'left',
    },
  },
]

// ============================================
// ROLES TOUR - Role & Permission Management
// ============================================
export const rolesTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '🛡️ Roles & Permissions',
      description: 'Define roles and their permissions to control access to different parts of the system.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="create-role-btn"], button:has-text("Add"), button:has-text("Create")',
    popover: {
      title: '➕ Create Role',
      description: 'Define a new role (e.g., "HR Manager", "Viewer") and specify which modules it can access.',
      side: 'left',
    },
  },
  {
    element: '.va-data-table, table',
    popover: {
      title: '📋 Role List',
      description: 'View all roles in the system. Built-in roles (Admin, User) cannot be deleted.',
      side: 'top',
    },
  },
  {
    element: '[class*="permission"], .va-checkbox',
    popover: {
      title: '✅ Permission Matrix',
      description: 'For each role, check which actions are allowed: Create, Read, Update, Delete, Approve, etc.',
      side: 'right',
    },
  },
]

// ============================================
// OFFICIAL BUSINESS TOUR
// ============================================
export const officialBusinessTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '🚗 Official Business',
      description: 'File OB requests for off-site work activities like client meetings, trainings, or conferences.',
      side: 'bottom',
    },
  },
  {
    element: '[data-tour="request-ob-btn"], button:has-text("Request")',
    popover: {
      title: '📝 File OB Request',
      description:
        'Submit an OB request with date, time, destination, and purpose. Attach supporting documents if needed.',
      side: 'left',
    },
  },
  {
    element: '.va-data-table, table',
    popover: {
      title: '📋 OB Requests',
      description: 'View all your official business requests and their approval status.',
      side: 'top',
    },
  },
]

// ============================================
// OFFSET TOUR - Compensatory Time Off
// ============================================
export const offsetTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '⏱️ Offset Management',
      description: 'Manage compensatory time off (CTO) earned from overtime work.',
      side: 'bottom',
    },
  },
  {
    element: '.offset-balance, [class*="balance"]',
    popover: {
      title: '💰 Offset Balance',
      description:
        'View your available offset hours. These are earned when OT is converted to time-off instead of pay.',
      side: 'right',
    },
  },
  {
    element: 'button:has-text("Use Offset")',
    popover: {
      title: '🏖️ Use Offset',
      description: 'Consume offset hours as time-off. Similar to leave but uses your earned offset credits.',
      side: 'left',
    },
  },
]

// ============================================
// PAYROLL SETTINGS TOUR
// ============================================
export const payrollSettingsTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '⚙️ Payroll Settings',
      description:
        'Configure payroll parameters: allowance types, deduction types, tax tables, and contribution rates.',
      side: 'bottom',
    },
  },
  {
    element: '.va-tabs, [role="tablist"]',
    popover: {
      title: '📑 Settings Tabs',
      description:
        'Navigate between: Allowance Types, Deduction Types, Government Contributions, and General Settings.',
      side: 'bottom',
    },
  },
  {
    element: '[class*="allowance"]',
    popover: {
      title: '💵 Allowance Types',
      description:
        "Define allowance types: Transportation, Meal, Communication, etc. Set if they're taxable and frequency.",
      side: 'right',
    },
  },
  {
    element: '[class*="deduction"]',
    popover: {
      title: '💸 Deduction Types',
      description: 'Define deduction categories: Government (SSS, PhilHealth), Loans, Benefits, etc.',
      side: 'right',
    },
  },
  {
    element: '[class*="contribution"]',
    popover: {
      title: '🏛️ Government Contributions',
      description: 'Configure SSS, PhilHealth, and Pag-IBIG contribution tables based on current government rates.',
      side: 'right',
    },
  },
]

// ============================================
// TIME IN/OUT TOUR
// ============================================
export const timeInOutTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '⏰ Time Clock',
      description: 'Record your daily time in and time out. Your attendance is automatically tracked.',
      side: 'bottom',
    },
  },
  {
    element: '[class*="clock"], .time-display',
    popover: {
      title: '🕐 Current Time',
      description: 'Shows the current date and time. Your clock action will be recorded with this timestamp.',
      side: 'bottom',
    },
  },
  {
    element: 'button:has-text("Clock In"), .va-button--success',
    popover: {
      title: '✅ Clock In',
      description: 'Click to record your time in when you start work. Button changes to Clock Out after you time in.',
      side: 'right',
    },
  },
  {
    element: 'button:has-text("Clock Out"), .va-button--danger',
    popover: {
      title: '🚪 Clock Out',
      description: 'Click to record your time out when leaving. Your work hours are calculated automatically.',
      side: 'right',
    },
  },
]

// ============================================
// HOLIDAYS TOUR
// ============================================
export const holidaysTour: TourStep[] = [
  {
    element: '.page-header, h1',
    popover: {
      title: '📅 Holiday Calendar',
      description: 'Manage company holidays. Holidays affect attendance calculations and payroll rates.',
      side: 'bottom',
    },
  },
  {
    element: 'button:has-text("Add Holiday")',
    popover: {
      title: '➕ Add Holiday',
      description: "Create a new holiday: Name, Date, Type (Regular or Special), and whether it's paid.",
      side: 'left',
    },
  },
  {
    element: '.va-calendar, [class*="calendar"]',
    popover: {
      title: '📆 Calendar View',
      description: 'Visual calendar showing all holidays. Regular holidays in red, special holidays in blue.',
      side: 'top',
    },
  },
]

// ============================================
// TOUR REGISTRY - Master List
// ============================================
export const tourRegistry: Record<string, { name: string; description: string; steps: TourStep[] }> = {
  dashboard: {
    name: 'Dashboard Tour',
    description: 'Learn to navigate the HRIS dashboard',
    steps: dashboardTour,
  },
  employees: {
    name: 'Employees Tour',
    description: 'Master employee management features',
    steps: employeesTour,
  },
  attendance: {
    name: 'Attendance Tour',
    description: 'Track and manage attendance',
    steps: attendanceTour,
  },
  payroll: {
    name: 'Payroll Tour',
    description: 'Process salaries and generate payslips',
    steps: payrollTour,
  },
  leave: {
    name: 'Leave Tour',
    description: 'Request and manage leave',
    steps: leaveTour,
  },
  overtime: {
    name: 'Overtime Tour',
    description: 'File and track overtime',
    steps: overtimeTour,
  },
  shifts: {
    name: 'Shifts Tour',
    description: 'Configure work shifts',
    steps: shiftsTour,
  },
  'work-schedules': {
    name: 'Work Schedules Tour',
    description: 'Manage employee schedules',
    steps: workSchedulesTour,
  },
  'org-chart': {
    name: 'Org Chart Tour',
    description: 'Explore organization structure',
    steps: orgChartTour,
  },
  reports: {
    name: 'Reports Tour',
    description: 'Generate HR reports',
    steps: reportsTour,
  },
  settings: {
    name: 'Settings Tour',
    description: 'Configure system settings',
    steps: settingsTour,
  },
  users: {
    name: 'Users Tour',
    description: 'Manage system users',
    steps: usersTour,
  },
  roles: {
    name: 'Roles Tour',
    description: 'Configure roles and permissions',
    steps: rolesTour,
  },
  permissions: {
    name: 'Permissions Tour',
    description: 'Configure roles and permissions',
    steps: rolesTour,
  },
  'official-business': {
    name: 'Official Business Tour',
    description: 'File OB requests',
    steps: officialBusinessTour,
  },
  offset: {
    name: 'Offset Tour',
    description: 'Manage compensatory time off',
    steps: offsetTour,
  },
  'payroll-settings': {
    name: 'Payroll Settings Tour',
    description: 'Configure payroll parameters',
    steps: payrollSettingsTour,
  },
  'time-in-out': {
    name: 'Time Clock Tour',
    description: 'Learn to clock in/out',
    steps: timeInOutTour,
  },
  holidays: {
    name: 'Holidays Tour',
    description: 'Manage company holidays',
    steps: holidaysTour,
  },
}
